/**
 * Team join requests API (spec 2026-09-10): a same-domain signup asks to join
 * an existing team from step 5 of the wizard; the owner approves or declines
 * from the Account page. Server: packages/server/src/routes/teamJoinRequests.ts,
 * mounted at /api/team-join-requests (its own prefix, because /teams/:id would
 * swallow a /teams/candidates).
 *
 * Every call passes `token`. Omitting it 401s the whole feature for every
 * signed-in user while the component tests, which mock this module, stay
 * green (the tokenSourcesApi incident).
 */
import { fetchApi } from './client';
import { log } from '@/lib/logger';
import { MUTATION_TIMEOUT_MS, SCREEN_READ_TIMEOUT_MS } from './requestTimeout';

log.info('[api/teamJoinRequests] Module loaded');

export interface JoinCandidate {
  teamId: string;
  name: string;
  memberCount: number;
  /** True when this user already has a pending request on the team. */
  requested: boolean;
}

export interface JoinCandidatesResponse {
  /** The user's work domain, or null when it is a public mailbox or unreadable (candidates is then empty). */
  domain: string | null;
  candidates: JoinCandidate[];
}

export interface JoinRequestSummary {
  id: string;
  teamId: string;
  status: 'pending' | 'approved' | 'declined';
  createdAt: string;
}

export interface PendingJoinRequest {
  id: string;
  requester: { name: string; email: string };
  createdAt: string;
}

export interface ApproveJoinRequestResult {
  id: string;
  status: 'approved';
  /** True when the requester was already on the team; no invitation was created. */
  alreadyMember: boolean;
  invitation: { id: string; email: string; role: string } | null;
}

export const teamJoinRequestsApi = {
  candidates: (token: string | undefined) =>
    fetchApi<JoinCandidatesResponse>('/team-join-requests/candidates', { token, timeoutMs: SCREEN_READ_TIMEOUT_MS }),

  // The wizard handles the server's refusals itself (feature off 403,
  // not-a-candidate 403, already-a-member 409) and declares them so a handled
  // refusal logs at warn instead of minting a fake $exception.
  create: (token: string | undefined, teamId: string) =>
    fetchApi<JoinRequestSummary>('/team-join-requests', {
      token,
      method: 'POST',
      timeoutMs: MUTATION_TIMEOUT_MS,
      body: JSON.stringify({ teamId }),
      expectedErrorStatuses: [403, 409],
    }),

  listForTeam: (token: string | undefined, teamId: string) =>
    fetchApi<PendingJoinRequest[]>(`/team-join-requests/team/${teamId}`, { token, timeoutMs: SCREEN_READ_TIMEOUT_MS }),

  // Approve shares the invite route's refusals (seat cap / lapsed plan 403,
  // already-resolved 409, email-provider 500); the Settings block renders each.
  approve: (token: string | undefined, requestId: string) =>
    fetchApi<ApproveJoinRequestResult>(`/team-join-requests/${requestId}/approve`, {
      token,
      method: 'POST',
      timeoutMs: MUTATION_TIMEOUT_MS,
      expectedErrorStatuses: [403, 409, 500],
    }),

  decline: (token: string | undefined, requestId: string) =>
    fetchApi<{ id: string; status: 'declined' }>(`/team-join-requests/${requestId}/decline`, {
      token,
      method: 'POST',
      timeoutMs: MUTATION_TIMEOUT_MS,
      expectedErrorStatuses: [403, 409],
    }),
};
